import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiCheck, FiInfo, FiAlertCircle } from 'react-icons/fi';
import { formatCurrency } from '../../utils/formatters';

export default function ApplyCreditModal({ isOpen, onClose, credit, openBills }) {
  const [applications, setApplications] = useState({});
  
  if (!isOpen || !credit) return null;

  const totalApplying = Object.values(applications).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  const remainingCredit = credit.balance - totalApplying;

  const handleApply = (billId, amount, max) => {
    const val = Math.min(parseFloat(amount) || 0, max, remainingCredit + (parseFloat(applications[billId]) || 0));
    setApplications({ ...applications, [billId]: val });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden flex flex-col border border-slate-200">
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <h2 className="text-xl font-black text-slate-900">Apply Vendor Credit</h2>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{credit.number} • {credit.vendorName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <div className="p-8 space-y-6">
          <div className="flex justify-between items-center p-5 bg-blue-600 rounded-2xl text-white shadow-lg">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Available Credit</p>
              <p className="text-2xl font-black">{formatCurrency(credit.balance)}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Remaining After Application</p>
              <p className="text-2xl font-black text-blue-200">{formatCurrency(remainingCredit)}</p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Outstanding Bills</h3>
            {openBills.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                <p className="text-sm font-bold text-slate-400">No open bills for this vendor.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {openBills.map(bill => (
                  <div key={bill.id} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl hover:border-blue-200 transition-all shadow-sm">
                    <div>
                      <p className="text-sm font-black text-slate-900">{bill.number}</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Due: {bill.dueDate} • Bal: {formatCurrency(bill.balance)}</p>
                    </div>
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                      <input 
                        type="number"
                        placeholder="0.00"
                        value={applications[bill.id] || ''}
                        onChange={(e) => handleApply(bill.id, e.target.value, bill.balance)}
                        className="w-full pl-7 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-black outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {remainingCredit < 0 && (
            <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-700 text-xs font-bold animate-pulse">
              <SafeIcon icon={FiAlertCircle} className="text-lg" />
              Application amount exceeds available credit.
            </div>
          )}
        </div>

        <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2.5 rounded-full font-black text-sm text-slate-500 hover:bg-slate-100 transition-all">Cancel</button>
          <button 
            disabled={totalApplying <= 0 || remainingCredit < 0}
            className="px-8 py-2.5 bg-slate-900 text-white rounded-full font-black text-sm transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <SafeIcon icon={FiCheck} /> Apply Credits
          </button>
        </div>
      </div>
    </div>
  );
}